import type { Currency } from '@ps/web-core-enums';
import { Translation } from '@ps/web-shared-components';
import React, { useEffect, useState } from 'react';
import { FormattedTime } from 'react-intl';
import { useSelector } from 'react-redux';
import { interval, lastValueFrom } from 'rxjs';
import { take, takeWhile } from 'rxjs/operators';

import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';
import { subscribe } from '@swf/core/api/subscribe';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import { select } from '@swf/core/store';

import pixLogo from '../../../../../../assets/logos/pix.png';
import type { DepositResult } from '../../../../../../types/Cashier';
import { DepositStatus } from '../../../../../../types/status';
import {
  generateDepositResult,
  isZeroExponentCurrency,
  trimWithoutRounding
} from '../../../../../../utils/cashier';
import Alert from '../../../../../Alert/Alert';
import CTAButton from '../../../../../CTAButton/CTAButton';

import {
  TXTCLI_Amount,
  TXTINT_BRAND_PokerStars,
  TXTWCL_Beneficiary_1,
  TXTWCL_Code_expires_inX_N0,
  TXTWCL_Copy_PIX_codeX,
  TXTWCL_Copy_and_paste_the_PIX_code_in_ELL,
  TXTWCL_Deposits_must_be_made_from_you_ELL,
  TXTWCL_Payment_details,
  TXTWCL_QR_code_expires_inX_N0,
  TXTWCL_Scan_the_QR_code_with_your_ban_ELL,
  TXTWCL_Your_transaction_is_being_proc_ELL
} from './PixRedirect.messages.json';
import { brand, button, container, info, logo, text, time } from './PixRedirect.scss';
import QrComponent from './QrComponent/QrComponent';

interface Props {
  amount: number;
  currency: Currency;
  qrCodeData?: string;
  handleStatusChange: () => void;
}

const PIX_TIMEOUT_TIME = 15 * 60;

const PixRedirect: React.FC<Props> = ({ amount, currency, qrCodeData, handleStatusChange }) => {
  const { data } = useWizardContext();
  const [timer, setTimer] = useState(PIX_TIMEOUT_TIME);
  const isPhoneDevice = useSelector(select.device.isPhone);

  const remainTimeDisplay = (
    <FormattedTime
      value={new Date(0, 0, 0, 0, Math.floor(timer / 60), timer % 60)}
      second="numeric"
      minute="numeric"
    />
  );

  useEffect(() => {
    const endTimer = new Date();
    endTimer.setSeconds(endTimer.getSeconds() + PIX_TIMEOUT_TIME);
    const depositSubscription = subscribe<{ body: DepositResult }>('/user/queue/cashier');
    const response = lastValueFrom(depositSubscription.pipe(take(1)));
    const outOfTime = interval(1000).pipe(takeWhile(() => Date.now() < endTimer.getTime()));

    const subscribeOutOfTime = outOfTime.subscribe(() =>
      setTimer(Math.ceil(Math.abs(endTimer.getTime() - Date.now()) / 1000))
    );

    Promise.race([response, lastValueFrom(outOfTime)]).then((res) => {
      const resultTransaction = res as { body: DepositResult };
      if (resultTransaction?.body) {
        data.depositResult = resultTransaction.body;
      } else {
        data.depositResult = generateDepositResult(DepositStatus.TIMEOUT, data.paySystemDetails);
      }
      handleStatusChange();
    });

    return () => {
      subscribeOutOfTime.unsubscribe();
    };
  }, [data, handleStatusChange]);

  const onCopyCode = () => {
    qrCodeData && navigator.clipboard.writeText(qrCodeData);
  };

  const displayMobile = (
    <>
      <div className={text}>
        <Translation tagName="h2" {...TXTWCL_Your_transaction_is_being_proc_ELL} />
        <Translation tagName="h3" {...TXTWCL_Copy_and_paste_the_PIX_code_in_ELL} />
      </div>
      <div className={logo}>
        <img src={pixLogo} alt="" />
      </div>
      <p className={time}>
        <Translation values={{ 0: remainTimeDisplay }} {...TXTWCL_Code_expires_inX_N0} />
      </p>
      <CTAButton
        className={button}
        type="button"
        onClick={onCopyCode}
        translation={TXTWCL_Copy_PIX_codeX}
      />
    </>
  );

  const displayWeb = (
    <>
      <div className={text}>
        <Translation tagName="h2" {...TXTWCL_Your_transaction_is_being_proc_ELL} />
        <Translation tagName="h3" {...TXTWCL_Scan_the_QR_code_with_your_ban_ELL} />
      </div>
      <div className={logo}>
        <img src={pixLogo} alt="" />
      </div>
      <p className={time}>
        <Translation values={{ 0: remainTimeDisplay }} {...TXTWCL_QR_code_expires_inX_N0} />
      </p>
      <QrComponent qrCodeData={qrCodeData} />
      <CTAButton
        className={button}
        type="button"
        onClick={onCopyCode}
        translation={TXTWCL_Copy_PIX_codeX}
      />
    </>
  );

  return (
    <>
      {isPhoneDevice ? displayMobile : displayWeb}
      <div className={text}>
        <Translation tagName="h4" {...TXTWCL_Payment_details} />
      </div>
      <div className={container}>
        <div>
          <Translation tagName="p" {...TXTCLI_Amount} />
        </div>
        <div className={brand}>
          <CurrencyAmount
            value={trimWithoutRounding(amount)}
            isZeroExponentCurrency={isZeroExponentCurrency(currency)}
            currency={currency}
            dollarValue
            currencyDisplay="symbol"
          />
        </div>
      </div>
      <div className={container}>
        <div>
          <Translation tagName="p" {...TXTWCL_Beneficiary_1} />
        </div>
        <div className={brand}>
          <Translation tagName="p" {...TXTINT_BRAND_PokerStars} />
        </div>
      </div>
      <Alert variant="info" className={info}>
        <div>
          <Translation {...TXTWCL_Deposits_must_be_made_from_you_ELL} />
        </div>
      </Alert>
    </>
  );
};

export default PixRedirect;
